import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { WIKI_ARTICLE_KIND, articleTitle, getTagValue, isValidWikiArticle } from '@/lib/nip54';
import type { WikilinkSuggestion } from '@/lib/wikilinkAutocomplete';

function toSuggestions(events: NostrEvent[]): WikilinkSuggestion[] {
  // Group by normalized `d` tag so multi-author articles collapse into one
  // suggestion (using the most recently edited version's title), with a
  // count of how many distinct authors have written it.
  const byD = new Map<string, { title: string; authors: Set<string>; newest: number }>();

  for (const event of events) {
    const d = getTagValue(event, 'd');
    if (!d) continue;
    const existing = byD.get(d);
    if (!existing) {
      byD.set(d, { title: articleTitle(event), authors: new Set([event.pubkey]), newest: event.created_at });
    } else {
      existing.authors.add(event.pubkey);
      if (event.created_at > existing.newest) {
        existing.newest = event.created_at;
        existing.title = articleTitle(event);
      }
    }
  }

  return [...byD.entries()].map(([slug, info]) => ({
    slug,
    title: info.title,
    versionCount: info.authors.size,
  }));
}

/**
 * Fetches wiki article titles matching `query`, for use in the `[[` autocomplete
 * dropdown. Debounced by the caller (see `useDjotEditor`'s trigger state) —
 * this hook itself just runs whenever `query`/`enabled` change, relying on
 * TanStack Query's built-in de-duplication and `staleTime` to avoid
 * hammering relays while the user types quickly.
 */
export function useWikilinkSuggestions(query: string, enabled: boolean) {
  const { nostr } = useNostr();
  const trimmed = query.trim();

  return useQuery({
    queryKey: ['wiki', 'wikilink-suggestions', trimmed],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(4000)]);

      if (!trimmed) {
        // Empty query right after typing `[[` — show recently-edited
        // articles as a starting point.
        const events = await nostr.query([{ kinds: [WIKI_ARTICLE_KIND], limit: 60 }], { signal });
        return toSuggestions(events.filter(isValidWikiArticle));
      }

      const [searched, recent] = await Promise.all([
        nostr.query([{ kinds: [WIKI_ARTICLE_KIND], search: trimmed, limit: 40 }], { signal }).catch(() => []),
        nostr.query([{ kinds: [WIKI_ARTICLE_KIND], limit: 200 }], { signal }).catch(() => []),
      ]);

      const needle = trimmed.toLowerCase();
      const matchedRecent = recent.filter((event) => articleTitle(event).toLowerCase().includes(needle));

      return toSuggestions([...searched, ...matchedRecent].filter(isValidWikiArticle));
    },
    enabled,
    staleTime: 15_000,
  });
}
